import { useState } from 'react'
import { useQuery, useQueryClient } from '@tanstack/react-query'
import { Link, useParams } from 'react-router-dom'
import { config, categories, products, tables, menuPreview } from '../lib/api.js'
import { openMenuWithPreview } from '../lib/menu-preview.js'
import { Card, Badge, Spinner, Modal } from '../components/ui/index.js'
import { useI18n } from '../lib/i18n.js'
import { LOCALE_NATIVE_NAMES, normalizeLocale } from '@self-restaurant/shared'
import { useRealtime } from '../lib/realtime.js'
import { LIVE_PRODUCTION_URLS, STAGING_MENU_DOMAIN, menuUrl } from '@self-restaurant/shared'

// Mesma base de menus que a API usa no QR (CF_MENU_DOMAIN, #285): produção
// https://pratzu.com/r/<slug>; painel de staging →
// https://landing-staging.pratzu.com/r/<slug>.
const menuDomain = () =>
  typeof window !== 'undefined' && window.location.hostname.includes('-staging')
    ? STAGING_MENU_DOMAIN
    : LIVE_PRODUCTION_URLS.menuDomain
const menuLink = (slug: string, path = '') => menuUrl(menuDomain(), slug, path)

// #293: o dono pré-visualiza o menu mesmo com o menu público pausado — o
// clique pede um token de prévia e abre o menu com ?preview= (o href fica
// para o clique do meio / copiar link, sem prévia)
const openPreview = (e: { preventDefault: () => void }, url: string) => {
  e.preventDefault()
  void openMenuWithPreview(url, menuPreview.create)
}

export function Dashboard() {
  const { slug } = useParams<{ slug: string }>()
  const { t } = useI18n()
  const [tablePickerOpen, setTablePickerOpen] = useState(false)

  const { data: cfg, isLoading: loadingCfg } = useQuery({
    queryKey: ['config'],
    queryFn: () => config.get(),
  })
  const { data: cats } = useQuery({ queryKey: ['categories'], queryFn: () => categories.list() })
  const { data: prods } = useQuery({ queryKey: ['products'], queryFn: () => products.list() })
  const { data: tbls } = useQuery({ queryKey: ['tables'], queryFn: () => tables.list() })
  // Tempo real (hotfix #288): o dashboard do dono assina o canal do tenant —
  // mesas/pedidos mudam sem recarregar. A API conecta com a role do token.
  const queryClient = useQueryClient()
  useRealtime({
    role: 'dashboard',
    onEvent: (ev) => {
      if (ev.type.startsWith('table.') || ev.type.startsWith('order.')) {
        void queryClient.invalidateQueries({ queryKey: ['tables'] })
      }
    },
  })

  if (loadingCfg) {
    return (
      <div className="flex items-center justify-center h-64">
        <Spinner className="w-8 h-8" />
      </div>
    )
  }

  const activeCats = cats?.filter((c) => c.is_active).length ?? 0
  const activeProds = prods?.filter((p) => p.is_active).length ?? 0
  const activeTables = tbls?.filter((tb) => tb.is_active).length ?? 0

  const base = `/r/${slug}`

  const stats = [
    {
      label: t('admin.dashboard.stats_categories'),
      value: activeCats,
      to: `${base}/menu`,
      color: 'bg-violet-50 text-violet-700',
    },
    {
      label: t('admin.dashboard.stats_products'),
      value: activeProds,
      to: `${base}/menu`,
      color: 'bg-blue-50 text-blue-700',
    },
    {
      label: t('admin.dashboard.stats_tables'),
      value: activeTables,
      to: `${base}/tables`,
      color: 'bg-emerald-50 text-emerald-700',
    },
  ]

  const quickLinks = [
    {
      to: `${base}/branding`,
      label: t('admin.dashboard.link_branding'),
      description: t('admin.dashboard.link_branding_desc'),
      emoji: '🎨',
    },
    {
      to: `${base}/menu`,
      label: t('admin.dashboard.link_menu'),
      description: t('admin.dashboard.link_menu_desc'),
      emoji: '🍽️',
    },
    {
      to: `${base}/combos`,
      label: t('admin.dashboard.link_combos'),
      description: t('admin.dashboard.link_combos_desc'),
      emoji: '🎁',
    },
    {
      to: `${base}/tables`,
      label: t('admin.dashboard.link_tables'),
      description: t('admin.dashboard.link_tables_desc'),
      emoji: '📱',
    },
  ]

  const activeTbls = tbls?.filter((tb) => tb.is_active) ?? []

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex items-start justify-between">
        <div>
          <h1 className="text-2xl font-bold text-gray-900">
            {cfg?.name ?? t('admin.dashboard.default_name')}
          </h1>
          <p className="text-sm text-gray-500 mt-0.5">{t('admin.dashboard.subtitle')}</p>
        </div>
        <Badge color={cfg?.is_open ? 'green' : 'red'} className="text-sm px-3 py-1">
          {cfg?.is_open ? t('admin.dashboard.open') : t('admin.dashboard.closed')}
        </Badge>
      </div>

      {/* Stats */}
      <div className="grid grid-cols-1 sm:grid-cols-3 gap-4">
        {stats.map((s) => (
          <Link key={s.label} to={s.to}>
            <div className={`rounded-xl p-5 ${s.color} hover:opacity-80 transition-opacity`}>
              <p className="text-3xl font-bold">{s.value}</p>
              <p className="text-sm font-medium mt-1">{s.label}</p>
            </div>
          </Link>
        ))}
      </div>

      {/* Quick links */}
      <Card title={t('admin.dashboard.quick_actions')}>
        <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
          {quickLinks.map((l) => (
            <Link
              key={l.to}
              to={l.to}
              className="flex items-center gap-4 p-4 rounded-xl border border-gray-100 hover:border-indigo-200 hover:bg-indigo-50/50 transition-colors"
            >
              <span className="text-2xl">{l.emoji}</span>
              <div>
                <p className="text-sm font-medium text-gray-900">{l.label}</p>
                <p className="text-xs text-gray-500">{l.description}</p>
              </div>
            </Link>
          ))}
        </div>
      </Card>

      {/* Visualizar menu */}
      <Card title={t('admin.dashboard.view_menu')}>
        <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
          <a
            href={menuLink(slug!)}
            target="_blank"
            rel="noopener noreferrer"
            data-testid="dashboard-view-menu"
            onClick={(e) => openPreview(e, menuLink(slug!))}
            className="flex items-center gap-4 p-4 rounded-xl border border-gray-100 hover:border-indigo-200 hover:bg-indigo-50/50 transition-colors"
          >
            <span className="text-2xl">🛍️</span>
            <div>
              <p className="text-sm font-medium text-gray-900">
                {t('admin.dashboard.takeaway_delivery')}
              </p>
              <p className="text-xs text-gray-500">{t('admin.dashboard.takeaway_delivery_desc')}</p>
            </div>
            <svg
              className="w-4 h-4 text-gray-400 ml-auto"
              viewBox="0 0 20 20"
              fill="none"
              stroke="currentColor"
              strokeWidth="1.5"
            >
              <path
                strokeLinecap="round"
                strokeLinejoin="round"
                d="M13.5 6H5.25A2.25 2.25 0 003 8.25v10.5A2.25 2.25 0 005.25 21h10.5A2.25 2.25 0 0018 18.75V10.5m-10.5 6L21 3m0 0h-5.25M21 3v5.25"
              />
            </svg>
          </a>

          <button
            data-testid="dashboard-table-menu"
            onClick={() => setTablePickerOpen(true)}
            className="flex items-center gap-4 p-4 rounded-xl border border-gray-100 hover:border-indigo-200 hover:bg-indigo-50/50 transition-colors text-left"
          >
            <span className="text-2xl">🪑</span>
            <div>
              <p className="text-sm font-medium text-gray-900">{t('admin.dashboard.table_menu')}</p>
              <p className="text-xs text-gray-500">{t('admin.dashboard.table_menu_desc')}</p>
            </div>
            <svg
              className="w-4 h-4 text-gray-400 ml-auto"
              viewBox="0 0 20 20"
              fill="none"
              stroke="currentColor"
              strokeWidth="1.5"
            >
              <path strokeLinecap="round" strokeLinejoin="round" d="M8.25 4.5l7.5 7.5-7.5 7.5" />
            </svg>
          </button>
        </div>
      </Card>

      {/* Status restaurante */}
      {cfg && (
        <Card title={t('admin.dashboard.status')}>
          <div className="grid grid-cols-2 sm:grid-cols-3 gap-4 text-sm">
            <div>
              <p className="text-gray-500">{t('admin.dashboard.primary_locale')}</p>
              {/* #428: nome nativo do idioma (código só no title) */}
              <p className="font-medium" title={cfg.primary_locale}>
                {LOCALE_NATIVE_NAMES[
                  normalizeLocale(cfg.primary_locale) as keyof typeof LOCALE_NATIVE_NAMES
                ] ?? cfg.primary_locale}
              </p>
            </div>
            <div>
              <p className="text-gray-500">{t('admin.dashboard.takeaway')}</p>
              <Badge color={cfg.allow_takeaway ? 'green' : 'gray'}>
                {cfg.allow_takeaway ? t('admin.common.active') : t('admin.common.inactive')}
              </Badge>
            </div>
            <div>
              <p className="text-gray-500">{t('admin.dashboard.delivery')}</p>
              <Badge color={cfg.allow_delivery ? 'green' : 'gray'}>
                {cfg.allow_delivery ? t('admin.common.active') : t('admin.common.inactive')}
              </Badge>
            </div>
            <div>
              <p className="text-gray-500">{t('admin.dashboard.tip')}</p>
              <p className="font-medium capitalize">{cfg.tip_mode}</p>
            </div>
            <div>
              <p className="text-gray-500">{t('admin.dashboard.tables_total')}</p>
              <p className="font-medium">{cfg.table_count}</p>
            </div>
          </div>
        </Card>
      )}
      {/* Modal: seleccionar mesa */}
      <Modal
        open={tablePickerOpen}
        onClose={() => setTablePickerOpen(false)}
        title={t('admin.dashboard.choose_table')}
        size="sm"
      >
        {activeTbls.length === 0 ? (
          <div className="text-center py-6">
            <p className="text-gray-500 text-sm">{t('admin.dashboard.no_active_tables')}</p>
            <Link
              to={`${base}/tables`}
              className="mt-2 inline-block text-sm text-indigo-600 hover:underline"
              onClick={() => setTablePickerOpen(false)}
            >
              {t('admin.dashboard.create_tables')}
            </Link>
          </div>
        ) : (
          <div className="grid grid-cols-3 gap-2">
            {activeTbls.map((tb) => (
              <a
                key={tb.id}
                data-testid={`dashboard-table-${tb.number}`}
                href={menuLink(slug!, `/table/${tb.qr_token}`)}
                target="_blank"
                rel="noopener noreferrer"
                onClick={(e) => {
                  setTablePickerOpen(false)
                  openPreview(e, menuLink(slug!, `/table/${tb.qr_token}`))
                }}
                className="flex flex-col items-center justify-center gap-1 p-3 rounded-xl border border-gray-200 hover:border-indigo-300 hover:bg-indigo-50 transition-colors"
              >
                <span className="text-xl">🪑</span>
                <span className="text-sm font-semibold text-gray-800">
                  {tb.label ?? t('admin.dashboard.table_number', { number: String(tb.number) })}
                </span>
                <span className="text-xs text-gray-400">#{tb.number}</span>
              </a>
            ))}
          </div>
        )}
      </Modal>
    </div>
  )
}
